import {
  type Pitch,
  type FingeringContext,
  ticks,
  barCapacity,
  midiOf,
  parsePitch,
  averageFret,
  tabToMidi,
} from '@simplenotation/theory';
import type { Score, Track, Beat, Note, BeatRef, TimeSignature, KeySignature, Clef, BarInfo } from './types';

/** Time signature in force at a bar. */
export function timeSigAt(score: Score, bar: number): TimeSignature {
  for (let i = Math.min(bar, score.bars.length - 1); i >= 0; i--) {
    const ts = score.bars[i]?.timeSig;
    if (ts) return ts;
  }
  return { num: 4, den: 4 };
}

/** Key signature in force at a bar. */
export function keySigAt(score: Score, bar: number): KeySignature {
  for (let i = Math.min(bar, score.bars.length - 1); i >= 0; i--) {
    const ks = score.bars[i]?.keySig;
    if (ks) return ks;
  }
  return { fifths: 0, mode: 'major' };
}

export function clefAt(track: Track, bar: number): Clef {
  if (track.clefChanges) {
    for (let i = bar; i >= 1; i--) {
      const c = track.clefChanges[i];
      if (c) return c;
    }
  }
  return track.clef;
}

const MIDDLE_LINE: Record<Clef, string> = {
  treble: 'B4',
  treble8vb: 'B4',
  bass: 'D3',
  alto: 'C4',
  tenor: 'A3',
  percussion: 'B4',
};

/** Written pitch on the middle staff line for a clef. */
export function middleLineOf(clef: Clef): Pitch {
  return parsePitch(MIDDLE_LINE[clef]);
}

export function barCapacityAt(score: Score, bar: number): number {
  return barCapacity(timeSigAt(score, bar));
}

export function voiceOf(score: Score, ref: Pick<BeatRef, 'track' | 'bar' | 'voice'>): Beat[] {
  return score.tracks[ref.track]?.bars[ref.bar]?.voices[ref.voice] ?? [];
}

export function beatAt(score: Score, ref: BeatRef): Beat | undefined {
  return voiceOf(score, ref)[ref.beat];
}

/** Sum of beat lengths in a voice (grace notes take no time). */
export function filledTicks(beats: readonly Beat[]): number {
  let t = 0;
  for (const b of beats) if (!b.grace) t += ticks(b.duration);
  return t;
}

/** Onset tick of each beat within its bar. */
export function beatOnsets(beats: readonly Beat[]): number[] {
  const out: number[] = [];
  let t = 0;
  for (const b of beats) {
    out.push(t);
    if (!b.grace) t += ticks(b.duration);
  }
  return out;
}

export function beatOnset(score: Score, ref: BeatRef): number {
  const beats = voiceOf(score, ref);
  let t = 0;
  for (let i = 0; i < ref.beat && i < beats.length; i++) if (!beats[i].grace) t += ticks(beats[i].duration);
  return t;
}

export function sameRef(a: BeatRef, b: BeatRef): boolean {
  return a.track === b.track && a.bar === b.bar && a.voice === b.voice && a.beat === b.beat;
}

/** Order two refs by (bar, beat) within the same track/voice. */
export function compareRef(a: Pick<BeatRef, 'bar' | 'beat'>, b: Pick<BeatRef, 'bar' | 'beat'>): number {
  return a.bar - b.bar || a.beat - b.beat;
}

/** Iterate every beat of a track voice in a bar range, in order. */
export function* beatsInRange(
  score: Score,
  track: number,
  voice: number,
  from: { bar: number; beat: number },
  to: { bar: number; beat: number },
): Generator<{ ref: BeatRef; beat: Beat }> {
  for (let bar = from.bar; bar <= to.bar; bar++) {
    const beats = voiceOf(score, { track, bar, voice });
    const start = bar === from.bar ? from.beat : 0;
    const end = bar === to.bar ? Math.min(to.beat, beats.length - 1) : beats.length - 1;
    for (let i = start; i <= end; i++) yield { ref: { track, bar, voice, beat: i }, beat: beats[i] };
  }
}

/** The last note before a ref in the same voice (for chords, the top note). Looks back across bars. */
export function previousPitch(score: Score, ref: BeatRef, includeCurrent = false): Pitch | null {
  let bar = ref.bar;
  let i = includeCurrent ? ref.beat : ref.beat - 1;
  while (bar >= 0) {
    const beats = voiceOf(score, { ...ref, bar });
    if (i >= beats.length) i = beats.length - 1;
    for (; i >= 0; i--) {
      const b = beats[i];
      if (!b.rest && b.notes.length) return topNote(b).pitch;
    }
    bar--;
    if (bar >= 0) i = Number.MAX_SAFE_INTEGER;
  }
  return null;
}

export function topNote(b: Beat): Note {
  let best = b.notes[0];
  for (const n of b.notes) if (midiOf(n.pitch) > midiOf(best.pitch)) best = n;
  return best;
}

export function bottomNote(b: Beat): Note {
  let best = b.notes[0];
  for (const n of b.notes) if (midiOf(n.pitch) < midiOf(best.pitch)) best = n;
  return best;
}

export function soundingMidi(note: Note, track: Track): number {
  return midiOf(note.pitch) + track.writtenToSounding;
}

export function isFretted(track: Track): boolean {
  return !!track.tuning && track.tuning.length > 0;
}

/** Fingering context at a beat: hand position from the previous two fretted beats. */
export function fingeringContextAt(score: Score, ref: BeatRef): FingeringContext {
  const track = score.tracks[ref.track];
  const ctx: FingeringContext = { tuning: track.tuning ?? [], capo: track.capo ?? 0 };
  const positions: { string: number; fret: number }[] = [];
  let found = 0;
  let prevString: number | undefined;
  let bar = ref.bar;
  let i = ref.beat - 1;
  while (bar >= 0 && found < 2) {
    const beats = voiceOf(score, { ...ref, bar });
    if (i >= beats.length) i = beats.length - 1;
    for (; i >= 0 && found < 2; i--) {
      const b = beats[i];
      if (b.rest || !b.notes.length) continue;
      const ps = b.notes.filter((n) => n.string !== undefined && n.fret !== undefined).map((n) => ({ string: n.string!, fret: n.fret! }));
      if (!ps.length) continue;
      if (found === 0 && ps.length === 1) prevString = ps[0].string;
      if (ps.some((p) => p.fret > 0)) {
        positions.push(...ps);
        found++;
      } else if (found === 0 && prevString === undefined) {
        prevString = ps[0].string;
      }
    }
    bar--;
    i = Number.MAX_SAFE_INTEGER;
  }
  ctx.handPos = averageFret(positions);
  ctx.prevString = prevString;
  return ctx;
}

export function noteSoundingFromTab(track: Track, string: number, fret: number): number {
  return tabToMidi(string, fret, track.tuning ?? [], track.capo ?? 0);
}

/** Number of bars that hold the bar info for repeats/endings and so on. */
export function barInfo(score: Score, bar: number): BarInfo {
  return score.bars[bar] ?? {};
}

/** True when a bar's voice-0 content exceeds its capacity. */
export function isOverfull(score: Score, track: number, bar: number): boolean {
  const cap = barCapacityAt(score, bar);
  const tb = score.tracks[track].bars[bar];
  return tb.voices.some((v) => filledTicks(v) > cap);
}

/** Display bar number (1-based, honouring pickups and overrides). */
export function displayBarNumbers(score: Score): number[] {
  const out: number[] = [];
  let n = score.bars[0]?.pickup ? 0 : 1;
  for (let i = 0; i < score.bars.length; i++) {
    const b = score.bars[i];
    if (b.numberOverride !== undefined) n = b.numberOverride;
    out.push(n);
    n += b.multiRest && b.multiRest > 1 ? b.multiRest : 1;
  }
  return out;
}
